import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnDestroy,
} from '@angular/core';

import {
  CompanyCostItem,
  RecurringCost,
  EnumService,
  ModalService,
  CompanyService,
  NameFilter,
  FileArrays,
  PaidByListItem,
  LocalStorageService,
  Company,
  NameIdPair,
  DataSyncService,
} from '@ops/shared-types';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription, switchMap, of, distinctUntilChanged, mergeMap } from 'rxjs';
import { Miscellanious } from '@ops/global-config';
import { OpsCustomCurrencyModalComponent, CustomCurrencyModalConfig } from '@ops/storybook';

@Component({
  selector: 'app-company-cost-add',
  templateUrl: './company-cost-add.component.html',
  styleUrls: ['./company-cost-add.component.scss'],
})
export class CompanyCostAddComponent implements OnInit, OnDestroy {
  @Input() set companyId(companyId: string) {
    this.companyCost = {
      company: companyId,
    };
  }
  @Input() id: string;
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('preset') set presetValue(preset: { field: string; value: string }) {
    if (!preset) return;
    if (preset.value?.toUpperCase() === 'COMPANY COSTS') return;
    this.companyCost[preset.field] = preset.value;
    if (preset.field?.toUpperCase() === 'ACCOUNTINGCATEGORY') {
      this.onCategorySelected(preset.value);
    }
  }

  @Output() createCompanyCost = new EventEmitter<{
    item: CompanyCostItem;
    files: FileArrays;
  }>();
  @Output() createRecurringCost = new EventEmitter<RecurringCost>();
  @Output() cancel = new EventEmitter<void>();
  @Output() paidByOptionsRefreshed = new EventEmitter<void>();

  selectedCompany$: Observable<NameFilter>;

  selectedCompanyId: string;

  accountingCategoryOptions: Array<string>;
  costTypeOptions: Array<string>;
  paymentMethodOptions = [];
  subcategoryOptionsData: Array<{ category: string; options: Array<string> }>;
  subcategoryOptions: Array<string>;
  assignToOptions: Array<string>;
  paidByOptions: Array<string>;
  frequencyOptions: Array<string>;
  statusOptions: Array<string>;
  currencyOptions: Array<string>;

  // Custom currency properties
  showCustomCurrencyForm: boolean = false;
  customCurrencyConfig: CustomCurrencyModalConfig;

  assignToValues: Array<NameIdPair>;

  companyCurrencyCode?: string;

  companyCost: CompanyCostItem;
  loadingOptions = false;
  count = 1;

  isValid = false;

  isRecurring: boolean;
  frequency: string;
  endDate?: Date;

  taxPercentage: string;
  expectedAmount: string;
  isTaxed: boolean;

  exchangeRate: string = '';
  isCurrencyEditable: boolean;

  private element: HTMLElement;

  baseCurrencyCode: string;

  paidByNameIdList: Array<PaidByListItem>;

  subscriptions: Array<Subscription> = [];

  files: FileArrays = {
    invoiceFiles: [],
    paymentFiles: [],
  };

  maxRecurringDate = new Date();

  constructor(
    private enumService: EnumService,
    private modalService: ModalService,
    private el: ElementRef,
    private companyService: CompanyService,
    private localStorageService: LocalStorageService,
    private translateService: TranslateService,
    private dataSyncService: DataSyncService
  ) {
    this.element = this.el.nativeElement;
    this.selectedCompany$ =
      this.companyService.selectedCompany$?.asObservable();
  }

  async ngOnInit() {
    this.maxRecurringDate.setUTCFullYear(new Date().getUTCFullYear() + 1);

    if (this.element) {
      this.element.style.display = 'none';
      this.element.focus();
    }
    if (!this.id) {
      console.error('@Input id is mandatory');
      return;
    }
    document.body.appendChild(this.element);
    this.modalService.add(this);
    // ✅ OPTIMIZED: Use cached data instead of refetching
    const sub = this.selectedCompany$
      .pipe(
        distinctUntilChanged((prev, curr) => prev?._id === curr?._id), // Avoid duplicate processing
        mergeMap(async (c) => {
          const companyIdMatch =
            c?._id || this.localStorageService.getCurrentCompany()?._id;
          
          // ✅ Use cached company data instead of refetching
          const cachedCompany = this.localStorageService.getCurrentCompany();
          this.companyId = companyIdMatch;
          this.companyCost.company = companyIdMatch;
          this.selectedCompanyId = companyIdMatch;
          
          // ✅ Use enum service caching instead of refetching
          // Note: Payment method options will be loaded in getAllPaidByOptions() below
          
          return cachedCompany; // Return cached data instead of fetching
        })
      )
      .subscribe((c: Company | null) => {
        // ✅ Use cached data
        this.baseCurrencyCode = c?.baseCurrencyCode;
        this.companyCurrencyCode = c?.baseCurrencyCode;
        // console.log(this.baseCurrencyCode);
      });

    this.subscriptions.push(sub);

    this.accountingCategoryOptions =
      this.enumService.getAccountingCategoryOptions();
    this.costTypeOptions = this.enumService.getCostTypeOptions();
    // ✅ FIXED: Load filtered payment method options on initialization
    await this.getAllPaidByOptions();
    this.subcategoryOptionsData = this.enumService.getSubcategoryOptions();
    this.assignToValues = await this.enumService.getAssignToOptions(
      this.localStorageService.getCurrentCompany()._id
    );
    this.assignToOptions = this.assignToValues.map((a) => a.name);
    this.statusOptions = this.enumService.getCostStatuseOptions();
    this.frequencyOptions = this.enumService.getRecurringFrequencyOptions();

    
  }

  ngOnDestroy(): void {
    this.modalService.remove(this.element.id);
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.element.remove();
  }

  changeCount(add = true) {
    if (add) {
      this.count += 1;
      if (this.count > 1) {
        this.isRecurring = false;
      }
      return;
    } else if (this.count > 1) {
      this.count -= 1;
      if (this.count > 1) {
        this.isRecurring = false;
      }
    }
    this.onInputChange(this.count);

    this.checkRequirements();
  }

  onInputChange(value: number) {
    if (value < 1) {
      this.count = 1;
    }
  }

  openModal(id: string) {
    this.modalService.open(id);
  }

  onSelectDate(selection: Date, dateType: string) {
    this.companyCost[dateType] = selection;
    this.checkRequirements();
  }

  onSelectEndDate(selection: Date) {
    this.endDate = selection;
    this.checkRequirements();
  }

  onSave() {
    if (
      !(
        this.files?.invoiceFiles?.length > 0 ||
        this.files?.paymentFiles?.length > 0
      ) &&
      this.companyCost.exchangeRate === 0
    ) {
      window.alert(this.translateService.instant('windowAlert.companyCostAdd.exchangeRateCannotBeZero'));
      return;
    }
    if (!this.validate()) {
      window.alert(this.translateService.instant('windowAlert.companyCostAdd.fieldsMarkedWithAsteriskAreRequired'));
      return;
    }

    this.companyCost.paymentMethod = this.companyCost.paymentMethod
      ? this.companyCost.paymentMethod
      : null;

    const companyCostPaidBy =
      this.paidByNameIdList?.find((p) => p.name === this.companyCost.paidBy)
        ?.id ?? null;
    // this.companyCost.paidBy =
    //   this.paidByNameIdList?.find((p) => p.name === this.companyCost.paidBy)
    //     ?.id ?? null;

    // pull to method, refactor
    let assignedTo: string;
    if (this.companyCost.assignedTo) {
      assignedTo = this.assignToValues.find(
        (a) => a.name === this.companyCost.assignedTo
      ).id;
    }

    this.companyCost.description = this.companyCost.description ?? '';
    this.companyCost.name = this.companyCost.name ?? '';
    this.companyCost.assignedTo = this.companyCost.assignedTo ?? '';

    if (this.isRecurring) {
      this.saveRecurringCost();
      return;
    }
    if (this.companyCost.status?.toUpperCase() === 'PAID') {
      this.companyCost.recordedAmount =
        this.companyCost.expectedAmount *
        (1 + (this.companyCost.taxPercentage ?? 0) / 100);
    }

    this.expectedAmount = (
      +this.expectedAmount - +this.expectedAmount
    ).toString(); //field only resets with this line

    setTimeout(() => {
      if (this.count != 1) {
        this.files = {
          invoiceFiles: [],
          paymentFiles: [],
        };
      }
      for (let index = 0; index < this.count; index++) {
        this.createCompanyCost.emit({
          item: {
            ...this.companyCost,
            assignedTo: assignedTo,
            paidBy: companyCostPaidBy,
          },
          files: this.files,
        });
      }
      this.resetItem();
      this.close();
    }, 10);
  }

  saveRecurringCost() {
    const companyCostPaidBy =
      this.paidByNameIdList?.find((p) => p.name === this.companyCost.paidBy)
        ?.id ?? null;

    const assignedTo =
      this.assignToValues.find((a) => a.name === this.companyCost.assignedTo)
        ?.id ?? null;

    const recurring: RecurringCost = {
      accountingCategory: this.companyCost.accountingCategory,
      company: this.companyCost.company,
      costType: this.companyCost.costType,
      endDate: new Date(this.endDate).toLocaleDateString('en-US') as any,
      expectedAmount: this.companyCost.expectedAmount,
      frequency: this.frequency,
      startDate: new Date(this.companyCost.dateOfBill).toLocaleDateString(
        'en-US'
      ) as any,
      subcategory: this.companyCost.subcategory,
      assignedTo: assignedTo,
      description: this.companyCost.description,
      name: this.companyCost.name,
      exchangeRate: this.companyCost.exchangeRate,
      currencyCode: this.companyCost.currencyCode,
      paymentDate: new Date(this.companyCost.paymentDate).toLocaleDateString(
        'en-US'
      ) as any,
      taxPercentage: this.companyCost.taxPercentage,
      status: this.companyCost.status,
      paymentMethod: this.companyCost.paymentMethod,
      paidBy: companyCostPaidBy,
    };
    this.expectedAmount = (
      +this.expectedAmount - +this.expectedAmount
    ).toString(); //field only resets with this line
    setTimeout(() => {
      this.createRecurringCost.emit(recurring);
      this.close();
    }, 10);
  }
  onCancel() {
    this.expectedAmount = (
      +this.expectedAmount - +this.expectedAmount
    ).toString(); //field only resets with this line

    this.exchangeRate = (+this.exchangeRate - +this.exchangeRate).toString(); //field only resets with this line

    setTimeout(() => {
      this.cancel.emit();
      this.close();
    }, 10);
  }

  open(): void {
    this.resetItem();
    this.element.style.display = 'flex';
    this.element.focus();
  }

  close(event?: any): void {
    this.isValid = false;
    this.resetItem();
    event?.stopPropagation();
    this.element.style.display = 'none';
  }

  resetItem() {
    this.companyCost = {
      dateOfBill: null,
      paymentDate: null,
      accountingCategory: '',
      subcategory: '',
      description: '',
      name: '',
      costType: '',
      paymentMethod: '',
      paidBy: '',
      status: '',
      assignedTo: '',
      hasReminder: false,
      expectedAmount: 0,
      taxPercentage: 0,
      recordedAmount: 0,
      exchangeRate: 0,
    };
    this.files = {
      invoiceFiles: [],
      paymentFiles: [],
    };
    this.endDate = null;
    this.frequency = null;
    this.isRecurring = false;
    this.count = 1;
    this.expectedAmount = '';
    this.taxPercentage = '';
    this.isTaxed = false;
    this.exchangeRate = '';
  }

  validate() {
    if (
      !this.companyCost.accountingCategory ||
      !this.companyCost.subcategory ||
      !this.companyCost.dateOfBill ||
      !this.companyCost.paymentDate
    ) {
      return false;
    }
    if (
      !this.isRecurring &&
      (this.files?.invoiceFiles?.length > 0 ||
        this.files?.paymentFiles?.length > 0)
    ) {
      return true;
    }
    if (
      !this.companyCost.costType ||
      this.count <= 0 ||
      !this.companyCost.paymentMethod ||
      !this.companyCost.paidBy ||
      !this.companyCost.expectedAmount ||
      !this.companyCost.status ||
      (this.isTaxed && !this.companyCost.taxPercentage) ||
      (this.isRecurring && (!this.frequency || !this.endDate)) ||
      !this.companyCost.exchangeRate
    ) {
      return false;
    }
    return true;
  }

  checkRequirements() {
    this.isValid = this.validate();
  }

  onCategorySelected(option: string) {
    // console.log(option);
    this.setSubcategoryOptions(option);
    this.checkRequirements();
  }
  onFrequencySelected(option: string) {
    // console.log(option);
    this.frequency = option;
    this.checkRequirements();
  }

  onEditCost(event: any) {
    this.companyCost.expectedAmount = +event.target.value.replaceAll(',', '');
    this.checkRequirements();
  }
  onToggleTax() {
    this.taxPercentage = this.isTaxed ? '0' : '10';
    this.companyCost.taxPercentage = +this.taxPercentage.replaceAll(',', '');
    this.checkRequirements();
  }

  onEditTax(event: any) {
    this.companyCost.taxPercentage = +event.target.value.replaceAll(',', '');
    this.checkRequirements();
  }

  onSelectCurrency(selection: string) {
    if (selection === '+ Add New') {
      this.toggleCustomCurrencyForm();
      return;
    }
    
    this.companyCost.currencyCode = selection;
    if (this.baseCurrencyCode === this.companyCost.currencyCode) {
      setTimeout(() => {
        this.exchangeRate = '1';
        this.companyCost.exchangeRate = 1;
        this.checkRequirements();
      }, 50);
      return;
    }
    this.exchangeRate = '';
    this.companyCost.exchangeRate = 0;
    this.checkRequirements();
  }

  onEditExchangeRate(value: number) {
    this.companyCost.exchangeRate = value ?? 1;
    this.checkRequirements();
  }

  calculateReciprocal(): void {
    if (this.companyCost?.exchangeRate && this.companyCost.exchangeRate !== 0) {
      this.companyCost.exchangeRate = 1 / this.companyCost.exchangeRate;
      this.onEditExchangeRate(this.companyCost.exchangeRate);
    }
  }

  private setSubcategoryOptions(category: string) {
    this.companyCost.subcategory = null;
    this.subcategoryOptions =
      this.subcategoryOptionsData.find(
        (d) => d.category?.toUpperCase() === (category?.toUpperCase() ?? '')
      )?.options ?? [];
    this.checkRequirements();
  }

  async getAllPaidByOptions() {
    const company = this.localStorageService.getCurrentCompany()?._id;
    
    try {
      // ✅ CHANGED: Use all payment method options (unfiltered) to show all methods
      // This allows users to see all payment methods even if no accounts/cards are configured
      this.paymentMethodOptions = this.enumService.getAllPaymentMethodOptions();
      
      this.checkRequirements();
    } catch (error) {
      console.error('Error loading payment method options:', error);
    }

    //TODO: use package
    this.currencyOptions = [
      '+ Add New', // Add custom currency option
      ...this.enumService.getCurrencyOptions()
    ];
  }

  // ✅ REMOVED: refreshPaidByOptionsFromCache method to avoid race conditions
  // Payment method options are now loaded only in getAllPaidByOptions()

  async setPaidByOptions(paymentMethod: string) {
    this.loadingOptions = true;
    this.companyCost.paidBy = null;
    // console.log(paymentMethod);
    const data = await this.enumService.getPaidByOptions(
      paymentMethod,
      this.localStorageService.getCurrentCompany()?._id ||
        this.selectedCompanyId
    );
    this.paidByNameIdList = data.paidByList;
    this.paidByOptions = data.optionsList;
    this.loadingOptions = false;
    this.checkRequirements();
  }

  onSelectPaymentMethod(paymentMethod: string) {
    this.setPaidByOptions(paymentMethod);
  }

  setPaidBy(value: string) {
    this.companyCost.paidBy = value;
    if (!this.companyCost.currencyCode) {
      this.companyCost.currencyCode = this.paidByNameIdList.find(
        (p) => p.name === value
      )?.currencyCode;
    }
    this.checkRequirements();
  }

  attachFiles(event: any, type: 'invoice' | 'payment') {
    let files: Array<File> = event.target.files as Array<File>;

    files = [...(files as Array<File>)];
    files.forEach((f, index, array) => {
      if (f.size > 1024 * 1024 * 15) {
        window.alert(`${f.name} ${this.translateService.instant('windowAlert.companyCostAdd.fileTooBig')}`);
        array.splice(index, 1);

        event.target.value = '';
        return;
      }
      if(this.files.invoiceFiles?.find((d) => (d.name === f.name) && (type === 'invoice'))
      || this.files.paymentFiles?.find((d) => (d.name === f.name) && (type === 'payment'))) {
        window.alert(this.translateService.instant('windowAlert.documentModal.fileAlreadyUploaded', { fileName: f.name }));
        array.splice(index, 1);
        event.target.value = '';
        return;
      }
    });

    switch (type) {
      case 'invoice':
        {
          if (files.length + this.files.invoiceFiles.length > 3) {
            window.alert(this.translateService.instant('windowAlert.companyCostAdd.cannotAttachMoreThanThreeInvoiceFiles'));
            return;
          }
          this.files.invoiceFiles.push(...files);
        }
        break;
      case 'payment':
        {
          if (files.length + this.files.paymentFiles.length > 3) {
            window.alert(
              this.translateService.instant('windowAlert.companyCostAdd.cannotAttachMoreThanThreePaymentConfirmationFiles')
            );
            return;
          }

          this.files.paymentFiles.push(...files);
        }
        break;
      default:
        break;
    }
    event.target.value = '';
    this.checkRequirements();
  }

  onPhotoInvoice(event: Event): void {
    if (this.files.invoiceFiles.length > 3) {
      window.alert(this.translateService.instant('windowAlert.companyCostAdd.cannotAttachMoreThanThreeInvoiceFiles'));
      return;
    }

    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      // console.log('Photo taken using camera:', file);
      this.files.invoiceFiles.push(file);
      this.checkRequirements();
      // Handle photo upload logic here
    }
  }

  onPhotoPayment(event: Event): void {
    if (this.files.paymentFiles.length > 3) {
      window.alert(this.translateService.instant('windowAlert.companyCostAdd.cannotAttachMoreThanThreePaymentConfirmationFiles'));
      return;
    }

    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      // console.log('Photo taken using camera:', file);
      this.files.paymentFiles.push(file);
      this.checkRequirements();
      // Handle photo upload logic here
    }
  }

  removeFile(file: File, type: 'invoice' | 'payment', index: number) {
    switch (type) {
      case 'invoice':
        this.files.invoiceFiles.splice(index, 1);
        break;
      case 'payment':
        this.files.paymentFiles.splice(index, 1);
        break;
      default: {
        window.alert(this.translateService.instant('windowAlert.companyCostAdd.fileMustBeInvoiceOrPayment'));
        return;
      }
    }
    window.alert(`${file.name} ${this.translateService.instant('windowAlert.companyCostAdd.fileRemoved')}`);
    this.checkRequirements();
  }

  get currencySymbol() {
    if (!this.companyCost.currencyCode || this.companyCost.currencyCode === '+ Add New') {
      return '';
    }
    return Miscellanious.currencySymbolFromCode(
      'en-US',
      this.companyCost.currencyCode
    );
  }

  get currencyExchangeSymbol() {
    return Miscellanious.currencySymbolFromCode(
      'en-US',
      this.companyCurrencyCode
    );
  }

  get expectedAmountWithTax() {
    return (
      (this.companyCost.expectedAmount ?? 0) *
      (1 + (this.companyCost.taxPercentage ?? 0) / 100)
    );
  }

  get expectedAmountWithTaxExchange() {
    return this.expectedAmountWithTax * this.companyCost.exchangeRate;
  }

  get expectedTaxAmount() {
    return (
      (this.companyCost.expectedAmount ?? 0) *
      ((this.companyCost.taxPercentage ?? 0) / 100)
    );
  }

  get expectedTaxAmountExchange() {
    return this.expectedTaxAmount * this.companyCost.exchangeRate;
  }

  get hasDifferentCurrency() {
    if (!this.companyCost.currencyCode) return false;
    if (this.companyCost.currencyCode && this.companyCurrencyCode) {
      if (this.companyCost.currencyCode !== this.companyCurrencyCode) {
        return true;
      }
    }
    return false;
  }

  get addNewOptionType(): 'bank-account' | 'credit-card' | null {
    if (this.companyCost.paymentMethod === 'Bank Transfer') {
      return 'bank-account';
    } else if (this.companyCost.paymentMethod === 'Credit Card') {
      return 'credit-card';
    }
    return null;
  }

  onAddNewBankAccount(): void {
    this.modalService.open('bank-account-create-modal');
  }

  onAddNewCreditCard(): void {
    this.modalService.open('credit-card-create-modal');
  }

  onBankAccountCreated(): void {
    // Refresh the paid by options to include the newly created bank account
    if (this.companyCost.paymentMethod) {
      this.setPaidByOptions(this.companyCost.paymentMethod);
    }
    // Notify parent component to refresh cashflow items
    this.paidByOptionsRefreshed.emit();
    
    // Notify other components that a bank account was created
    this.dataSyncService.notifyCreditCardCreated();
  }

  onCreditCardCreated(): void {
    // Refresh the paid by options to include the newly created credit card
    if (this.companyCost.paymentMethod) {
      this.setPaidByOptions(this.companyCost.paymentMethod);
    }
    // Notify parent component to refresh cashflow items
    this.paidByOptionsRefreshed.emit();
    
    // Notify other components that a credit card was created
    this.dataSyncService.notifyCreditCardCreated();
  }

  // Custom Currency Methods
  toggleCustomCurrencyForm(): void {
    this.showCustomCurrencyForm = !this.showCustomCurrencyForm;
    if (this.showCustomCurrencyForm) {
      this.customCurrencyConfig = {
        excludeCurrencyCodes: [this.baseCurrencyCode],
        excludeReason: 'This currency is already the base currency',
        onSuccess: {
          updateField: 'currencyCode'
        }
      };
    }
  }

  onCustomCurrencyAdded(event: { currencyCode: string; updateField?: string; additionalData?: any }): void {
    // Refresh currency options to include the new custom currency
    this.currencyOptions = [
      '+ Add New', // Add custom currency option
      ...this.enumService.getCurrencyOptions()
    ];
    
    // Auto-select the newly added currency
    this.companyCost.currencyCode = event.currencyCode;
    
    // Set exchange rate to 1 if it's the base currency, otherwise reset
    if (this.baseCurrencyCode === this.companyCost.currencyCode) {
      setTimeout(() => {
        this.exchangeRate = '1';
        this.companyCost.exchangeRate = 1;
        this.checkRequirements();
      }, 50);
    } else {
      this.exchangeRate = '';
      this.companyCost.exchangeRate = 0;
      this.checkRequirements();
    }
    
    // Close the modal
    this.showCustomCurrencyForm = false;
  }

  onCustomCurrencyModalClosed(): void {
    this.showCustomCurrencyForm = false;
  }
}
